import { Paperclip, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';

import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Tooltip } from '../../../../components/Tooltip';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { saveBlob } from '../../../../lib/saveBlob';
import { evaluationsApi } from '../praiseApi';
import { AWARD_STAGE, type AwardDetail, type AwardStageDocument, type AwardStageValue } from '../praiseTypes';
import { StageDocumentPreviewModal, UploadStageDocumentModal } from './StageDocumentModals';

/** Each stage's name and the key that writes its papers — the route the server gates on. */
const STAGE: Record<AwardStageValue, { name: string; permission: string }> = {
  [AWARD_STAGE.Deliberation]: { name: 'Deliberation', permission: 'award-evaluations.deliberate' },
  [AWARD_STAGE.FinalAwarding]: { name: 'Final awarding', permission: 'award-evaluations.award' },
};

/** A file's action glyph — the 201 file Documents panel's 32px control, for its reason. */
const GLYPH_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 ' +
  'hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent';

/**
 * One stage's papers as a value cell — **the 201 file's `DocumentsPanel` is the standard** for an
 * attachment list (03 Nov 2026), so this is its shape: the description with Upload at the OUTER RIGHT,
 * then `Not submitted` or a line per file — `Paperclip`, the name OPENING the preview (where the file's
 * facts live), Delete revealed on the line's hover. SEVERAL files per stage, so Upload ADDS.
 *
 * Upload and Delete carry the STAGE's own key, hidden without it; disabled for STATE with why.
 */
export function useStageDocumentsRow({
  award,
  stage,
  description,
  closedReason,
  onChanged,
}: {
  award: AwardDetail;
  stage: AwardStageValue;
  description: string;
  /** Why this stage's papers cannot change now, or null while it is open. */
  closedReason: string | null;
  onChanged: (next: AwardDetail) => void;
}) {
  const toast = useToast();
  const canWrite = usePermission(STAGE[stage].permission);

  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState<AwardStageDocument | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const files = (award.stageDocuments ?? []).filter((d) => d.stage === stage);

  async function remove(document: AwardStageDocument) {
    setBusyId(document.id);

    try {
      onChanged(await evaluationsApi.removeStageDocument(award.id, stage, document.id));
      toast.success(`${document.fileName} was removed.`);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not remove that document.');
    } finally {
      setBusyId(null);
    }
  }

  async function download(document: AwardStageDocument) {
    try {
      const { blob } = await evaluationsApi.downloadStageDocument(award.id, document.id);
      saveBlob(blob, document.fileName);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download that document.');
    }
  }

  const row = {
    label: 'Documents',
    value: (
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <span className="min-w-0 text-xs text-slate-400">{description}</span>

          {canWrite ? (
            <Tooltip label={closedReason ?? 'Upload'}>
              <button
                type="button"
                id={`award-stage-${stage}-upload`}
                onClick={() => setUploading(true)}
                disabled={closedReason != null}
                aria-label={`Upload a ${STAGE[stage].name.toLowerCase()} document`}
                className={GLYPH_BUTTON + ' hover:text-slate-700'}
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : null}
        </div>

        {files.length === 0 ? (
          <span className="text-slate-400">Not submitted</span>
        ) : (
          files.map((document) => (
            <span key={document.id} className="group/line flex min-w-0 items-center gap-2">
              <Paperclip className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />

              <span className="flex min-w-0 flex-1 items-center">
                <button
                  type="button"
                  onClick={() => setPreviewing(document)}
                  disabled={busyId === document.id}
                  className="min-w-0 truncate text-left font-medium text-slate-800 hover:underline disabled:text-slate-400"
                >
                  {document.fileName}
                </button>
              </span>

              <PermissionGate permission={STAGE[stage].permission}>
                <span className="flex shrink-0 items-center opacity-0 focus-within:opacity-100 group-hover/line:opacity-100 [@media(hover:none)]:opacity-100">
                  <Tooltip label={closedReason ?? 'Delete'}>
                    <button
                      type="button"
                      onClick={() => void remove(document)}
                      disabled={busyId === document.id || closedReason != null}
                      aria-label={`Delete ${document.fileName}`}
                      className={GLYPH_BUTTON + ' hover:text-red-600'}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                </span>
              </PermissionGate>
            </span>
          ))
        )}
      </div>
    ),
  };

  const modals = (
    <>
      {uploading ? (
        <UploadStageDocumentModal
          award={award}
          stage={stage}
          onClose={() => setUploading(false)}
          onSaved={(next) => {
            setUploading(false);
            toast.success(`The document was attached to the ${STAGE[stage].name.toLowerCase()}.`);
            onChanged(next);
          }}
        />
      ) : null}

      {previewing ? (
        <StageDocumentPreviewModal
          award={award}
          document={previewing}
          onDownload={() => void download(previewing)}
          onClose={() => setPreviewing(null)}
        />
      ) : null}
    </>
  );

  return { row, modals };
}

